import * as React from 'react';

import {NutAnalyticsClient} from 'nutshell-core/nut-analytics-client';

import {Button} from '../button/button';
import {RemoveButton} from '../button/remove-button/remove-button';
import {Card} from '../card/card';
import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {PlayButtonIcon} from '../icon/svg/play-button-icon';
import {MessageGroup} from '../message-group/message-group';
import {YouTubeModal} from '../video/youtube-modal';

import styles from './cta-video-banner.module.css';

type Props = {
    /** Optional title to include on first line */
    title?: string;
    /** Body text of the CTA. Must be included. */
    text: string;
    /** Secondary action text, styled in blue on the right. */
    buttonText: string;
    /** Optional id to add to the action button */
    buttonId?: string;
    /** Youtube videoId that'll be played when the thumbnail is clicked on */
    /** IMPORTANT: this video must be in the https://www.youtube.com/playlist?list=PLoLQikdPun7d2d3fahIfUni9854Zm1DxS playlist */
    /** because we always reference that playlist in code */
    videoId: string;
    /** Thumbnail image source, since we need to provide our own */
    thumbnailImageSource: string;
    /** Callback to execute when video thumbnail is clicked */
    onPrimaryAction: () => void;
    /** Including this callback adds a dismiss button, and onDismiss is called after animating out. */
    onDismiss?: () => void;
    /** Instantiated analytics client class for monitoring these video clicks/views */
    analyticsClient: NutAnalyticsClient;
    /** True to make play button dark (default is light), ideal for majorly white thumbnails */
    playButtonIsDark?: boolean;
};

type State = {
    isClosing: boolean;
    isModalOpen: boolean;
};

/**
 * Use this component when you want the user to perform an action, or
 * visit a support page to learn more about upgrading, or something similar.
 */
export class CTAVideoBanner extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isClosing: false,
            isModalOpen: false,
        };
    }

    render() {
        const bodyText = (
            <>
                {this.props.text}{' '}
                <span className={styles['primary-action']}>
                    {/* @ts-expect-error */}
                    {this.props.onPrimaryAction ? (
                        <Button
                            id={this.props.buttonId}
                            variant='text'
                            size='big'
                            onClick={this.handlePrimaryButtonClick}
                        >
                            {this.props.buttonText}
                        </Button>
                    ) : (
                        this.props.buttonText
                    )}
                </span>
            </>
        );

        return (
            <>
                <Card hasBorder={true}>
                    <div
                        className={
                            this.state.isClosing ? styles.closing : styles['cta-video-banner']
                        }
                    >
                        <div>
                            <div onClick={this.openVideoModal} className={styles.thumbnail}>
                                <img src={this.props.thumbnailImageSource} alt='Video thumbnail' />
                                <div className={styles['play-button']}>
                                    <PlayButtonIcon
                                        size={32}
                                        wrapWithDiv={false}
                                        color={this.props.playButtonIsDark ? colors.greyDk : '#fff'}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles['message-container']}>
                            <MessageGroup
                                title={this.props.title}
                                text={bodyText}
                                maxWidth={10000} /* No max width! */
                            />
                        </div>
                        {this.props.onDismiss ? (
                            <div className={styles['remove-button']}>
                                <RemoveButton onClick={this.handleRemoveClick} />
                            </div>
                        ) : null}
                    </div>
                </Card>
                {this.state.isModalOpen ? (
                    <YouTubeModal videoId={this.props.videoId} onClose={this.closeVideoModal} />
                ) : undefined}
            </>
        );
    }

    openVideoModal = () => {
        this.setState({isModalOpen: true});

        this.props.analyticsClient.track(
            this.props.analyticsClient.EVENTS.TRACK_VIDEO_POPUP_VIEWED,
            {
                videoId: this.props.videoId,
            }
        );
    };

    closeVideoModal = () => {
        this.setState({isModalOpen: false});
    };

    handlePrimaryButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (this.props.onPrimaryAction) {
            this.props.onPrimaryAction();
        }
    };

    handleRemoveClick = () => {
        this.setState({isClosing: true}, () => {
            // Not using handleTransitionEnd prop on wrapper
            // because that'll catch children opacity transitions
            // like hovering the play-button, for example.
            setTimeout(() => {
                this.handleTransitionEnd();
            }, 300);
        });
    };

    handleTransitionEnd = () => {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };
}
